import { useEffect, useRef, useState, useCallback } from 'react';
import { Loader2, X, ShieldCheck } from 'lucide-react';

const REVOLUT_PUBLIC_KEY = process.env.REACT_APP_REVOLUT_PUBLIC_KEY;
const REVOLUT_MODE = process.env.REACT_APP_REVOLUT_MODE || 'prod';

export const RevolutPayment = ({ orderToken, depositAmount, onSuccess, onError, onCancel, lang }) => {
  const containerRef = useRef(null);
  const checkoutRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const initCheckout = useCallback(async () => {
    if (!orderToken || !containerRef.current || checkoutRef.current) return;

    try {
      const RevolutCheckout = (await import('@revolut/checkout')).default;

      checkoutRef.current = await RevolutCheckout(orderToken, REVOLUT_MODE);

      const card = checkoutRef.current.createCardField({
        target: containerRef.current,
        hidePostcodeField: true,
        locale: lang === 'fr' ? 'fr' : 'en',
        onSuccess() {
          if (onSuccess) onSuccess();
        },
        onError(err) {
          console.error('Revolut payment error:', err);
          if (onError) onError(err);
        },
        onCancel() {
          if (onCancel) onCancel();
        },
        styles: {
          default: {
            color: '#ffffff',
            fontSize: '15px',
            fontFamily: 'Inter, system-ui, sans-serif',
            '::placeholder': { color: 'rgba(255, 255, 255, 0.35)' },
          },
          autofill: { color: '#C5A059' },
        },
      });

      checkoutRef.current._cardField = card;
      setLoading(false);
    } catch (err) {
      console.error('Revolut init error:', err);
      setError(err.message || 'Erreur initialisation paiement');
      setLoading(false);
    }
  }, [orderToken, lang, onSuccess, onError, onCancel]);

  useEffect(() => {
    initCheckout();
    return () => {
      if (checkoutRef.current) {
        try {
          checkoutRef.current.destroy();
        } catch (e) { /* ignore */ }
        checkoutRef.current = null;
      }
    };
  }, [initCheckout]);

  const handleSubmitPayment = useCallback(() => {
    if (checkoutRef.current?._cardField) {
      checkoutRef.current._cardField.submit({
        name: 'Client Interflex',
      });
    }
  }, []);

  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4">
        <div className="bg-[#0a0a0a] border border-white/10 p-8 max-w-md w-full text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button onClick={onCancel} className="btn-secondary">
            {lang === 'fr' ? 'Fermer' : 'Close'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4" data-testid="revolut-payment-modal">
      <div className="bg-[#0a0a0a] border border-[#C5A059]/20 w-full max-w-md relative">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <ShieldCheck size={20} className="text-[#C5A059]" />
            <div>
              <h3 className="text-white text-sm font-medium">
                {lang === 'fr' ? 'Paiement sécurisé' : 'Secure Payment'}
              </h3>
              <p className="text-white/40 text-xs">Revolut Business</p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="text-white/40 hover:text-white transition-colors p-1"
            data-testid="close-payment-modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Amount */}
        <div className="p-5 border-b border-white/10 text-center">
          <p className="text-white/50 text-xs uppercase tracking-wider mb-1">
            {lang === 'fr' ? 'Acompte à payer' : 'Deposit to pay'}
          </p>
          <p className="text-[#C5A059] text-3xl font-light" data-testid="payment-amount">
            {depositAmount.toFixed(2)}€
          </p>
        </div>

        {/* Card Field Container */}
        <div className="p-5">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-[#C5A059]" size={24} />
              <span className="text-white/50 ml-3 text-sm">
                {lang === 'fr' ? 'Chargement...' : 'Loading...'}
              </span>
            </div>
          )}
          <div
            ref={containerRef}
            id="revolut-card-field"
            className="min-h-[120px]"
            style={{
              '--rc-input-background': '#111111',
              '--rc-input-border': 'rgba(255,255,255,0.1)',
              '--rc-input-border-focus': '#C5A059',
            }}
            data-testid="revolut-card-container"
          />
        </div>

        {/* Pay Button */}
        <div className="p-5 pt-0">
          <button
            onClick={handleSubmitPayment}
            disabled={loading}
            className="btn-primary w-full flex items-center justify-center gap-2 py-4"
            data-testid="revolut-pay-btn"
          >
            <ShieldCheck size={18} />
            {lang === 'fr' ? `Payer ${depositAmount.toFixed(2)}€` : `Pay ${depositAmount.toFixed(2)}€`}
          </button>
        </div>

        {/* Security Note */}
        <div className="px-5 pb-5">
          <p className="text-white/30 text-xs text-center">
            {lang === 'fr'
              ? 'Paiement 100% sécurisé via Revolut Business'
              : '100% secure payment via Revolut Business'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default RevolutPayment;
